import { useState } from 'react';
import { FileExcelFilled } from '@ant-design/icons';
import { Col, Button, Tooltip, message } from 'antd';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { generateQueryString } from '@qlibs/react-components';
import useGroup from '../../../zustand/useGroup';
import useAppliedReportFilter from '../../../hooks/useAppliedReportFilter';
import useReportLog from '../../../hooks/useReportLog';
import { httpRequest } from '../../../helpers/api';
import useReportDownload from '../../../zustand/useReportDownload';
import { reportDayjs } from '../../../helpers/reportRange';
import { createBucketLookup, latToDMS, longToDMS } from './helperFunction';
import { getReportRangeError } from '../helpers/reportIntervalLimit';

// Flip this to true to download the xlsx from GET /report-log/export-xlsx
// using the active filter. The default keeps the browser export.
export const USE_BACKEND_DATA_LOG_XLSX = true;

interface IProps {
  columnsFiltered?: any[];
  dataSource: any[];
  loading: boolean;
}

type ExportColumnKind = 'number' | 'text';

type OperationModeEngine = 'port' | 'starboard' | 'center';

type OperationModeField = 'mode' | 'consRate';

interface ExportColumnDef {
  header: string;
  key: string;
  kind: ExportColumnKind;
  operationMode?: {
    engine: OperationModeEngine;
    field: OperationModeField;
    clutch?: boolean;
  };
}

const AE_FUEL_CONS_KEY = 'aeFuelCons';

const EXPORT_COLUMNS: ExportColumnDef[] = [
  { header: 'Date Time', key: 'dateTime', kind: 'text' },
  { header: 'Latitude', key: 'gps_latitude', kind: 'text' },
  { header: 'Longitude', key: 'gps_longitude', kind: 'text' },
  { header: 'Speed', key: 'gps_speed', kind: 'number' },
  { header: 'Distance', key: 'gps_distance', kind: 'number' },
  { header: 'Heading', key: 'gps_heading', kind: 'number' },
  { header: 'Port Rpm', key: 'port_Rpm', kind: 'number' },
  { header: 'Port Engine Status', key: 'port_EngineStatus', kind: 'text' },
  { header: 'Port Ops Mode', key: 'port_OperationMode', kind: 'text' },
  { header: 'Port Running Hour', key: 'port_RunningTime', kind: 'text' },
  { header: 'Port Clutch', key: 'port_Clutch', kind: 'number' },
  { header: 'Port Clutch Ops Mode', key: 'port_ClutchOperationMode', kind: 'text' },
  { header: 'Port Clutch Running Hour', key: 'port_ClutchRunningTime', kind: 'text' },
  { header: 'Port Clutch Status', key: 'port_ClutchStatus', kind: 'text' },
  { header: 'Port In Volume Total', key: 'fmPortIn_VolumeTotal', kind: 'number' },
  { header: 'Port In Flow', key: 'fmPortIn_InFlow', kind: 'number' },
  { header: 'Port Out Volume Total', key: 'fmPortOut_VolumeTotal', kind: 'number' },
  { header: 'Port Out Flow', key: 'fmPortOut_InFlow', kind: 'number' },
  { header: 'Port In Temp', key: 'fmPortIn_Temperature', kind: 'number' },
  { header: 'Port Out Temp', key: 'fmPortOut_Temperature', kind: 'number' },
  { header: 'Port In Dens', key: 'fmPortIn_InDensity', kind: 'number' },
  { header: 'Port Out Dens', key: 'fmPortOut_InDensity', kind: 'number' },
  { header: 'Port Fuel Cons', key: 'fmPort_FuelConsumption', kind: 'number' },
  { header: 'Stb Rpm', key: 'starboard_Rpm', kind: 'number' },
  { header: 'Stb Engine Status', key: 'starboard_EngineStatus', kind: 'text' },
  { header: 'Stb Ops Mode', key: 'starboard_OperationMode', kind: 'text' },
  { header: 'Stb Running Hour', key: 'starboard_RunningTime', kind: 'text' },
  { header: 'Stb Clutch', key: 'starboard_Clutch', kind: 'number' },
  { header: 'Stb Clutch Ops Mode', key: 'starboard_ClutchOperationMode', kind: 'text' },
  { header: 'Stb Clutch Running Hour', key: 'starboard_ClutchRunningTime', kind: 'text' },
  { header: 'Stb Clutch Status', key: 'starboard_ClutchStatus', kind: 'text' },
  { header: 'Stb In Volume Total', key: 'fmStarboardIn_VolumeTotal', kind: 'number' },
  { header: 'Stb In Flow', key: 'fmStarboardIn_InFlow', kind: 'number' },
  { header: 'Stb Out Volume Total', key: 'fmStarboardOut_VolumeTotal', kind: 'number' },
  { header: 'Stb Out Flow', key: 'fmStarboardOut_InFlow', kind: 'number' },
  { header: 'Stb In Temp', key: 'fmStarboardIn_Temperature', kind: 'number' },
  { header: 'Stb Out Temp', key: 'fmStarboardOut_Temperature', kind: 'number' },
  { header: 'Stb In Dens', key: 'fmStarboardIn_InDensity', kind: 'number' },
  { header: 'Stb Out Dens', key: 'fmStarboardOut_InDensity', kind: 'number' },
  { header: 'Stb Fuel Cons', key: 'fmStarboard_FuelConsumption', kind: 'number' },
  { header: 'Center Rpm', key: 'center_Rpm', kind: 'number' },
  { header: 'Center Engine Status', key: 'center_EngineStatus', kind: 'text' },
  { header: 'Center Ops Mode', key: 'center_OperationMode', kind: 'text' },
  { header: 'Center Running Hour', key: 'center_RunningTime', kind: 'text' },
  { header: 'Center Clutch', key: 'center_Clutch', kind: 'number' },
  { header: 'Center Clutch Ops Mode', key: 'center_ClutchOperationMode', kind: 'text' },
  { header: 'Center Clutch Running Hour', key: 'center_ClutchRunningTime', kind: 'text' },
  { header: 'Center Clutch Status', key: 'center_ClutchStatus', kind: 'text' },
  { header: 'Me Fuel Cons', key: 'meFuelCons', kind: 'number' },
  { header: 'Ae Fuel Cons', key: AE_FUEL_CONS_KEY, kind: 'number' },
  { header: 'Ae1 Run Hour', key: 'ae1_RunningTime', kind: 'text' },
  { header: 'Ae1 Fuel Cons', key: 'ae1_FuelConsumption', kind: 'number' },
  { header: 'Ae2 Run Hour', key: 'ae2_RunningTime', kind: 'text' },
  { header: 'Ae2 Fuel Cons', key: 'ae2_FuelConsumption', kind: 'number' },
  { header: 'Ae3 Run Hour', key: 'ae3_RunningTime', kind: 'text' },
  { header: 'Ae3 Fuel Cons', key: 'ae3_FuelConsumption', kind: 'number' },
  { header: 'Total Fuel Cons', key: 'totalFuelCons', kind: 'number' },
];

// Shared compact export used by RAS, Kanaka, and BRL. Port Ops Mode is the
// mode on the data-log row. Ae Fuel Cons stays the ae1+ae2+ae3 sum.
const RAS_EXPORT_COLUMNS: ExportColumnDef[] = [
  { header: 'Date Time', key: 'dateTime', kind: 'text' },
  { header: 'Latitude', key: 'gps_latitude', kind: 'text' },
  { header: 'Longitude', key: 'gps_longitude', kind: 'text' },
  { header: 'Speed', key: 'gps_speed', kind: 'number' },
  { header: 'Distance', key: 'gps_distance', kind: 'number' },
  { header: 'Heading', key: 'gps_heading', kind: 'number' },
  { header: 'Port Rpm', key: 'port_Rpm', kind: 'number' },
  { header: 'Port Running Hour', key: 'port_RunningTime', kind: 'text' },
  { header: 'Port Ops Mode', key: 'port_OperationMode', kind: 'text' },
  { header: 'Port Fuel Cons', key: 'fmPort_FuelConsumption', kind: 'number' },
  { header: 'Stb Rpm', key: 'starboard_Rpm', kind: 'number' },
  { header: 'Stb Running Hour', key: 'starboard_RunningTime', kind: 'text' },
  { header: 'Stb Fuel Cons', key: 'fmStarboard_FuelConsumption', kind: 'number' },
  { header: 'Me Fuel Cons', key: 'meFuelCons', kind: 'number' },
  { header: 'Ae Fuel Cons', key: AE_FUEL_CONS_KEY, kind: 'number' },
  { header: 'Ae1 Run Hour', key: 'ae1_RunningTime', kind: 'text' },
  { header: 'Ae1 Fuel Cons', key: 'ae1_FuelConsumption', kind: 'number' },
  { header: 'Ae2 Run Hour', key: 'ae2_RunningTime', kind: 'text' },
  { header: 'Ae2 Fuel Cons', key: 'ae2_FuelConsumption', kind: 'number' },
  { header: 'Ae3 Run Hour', key: 'ae3_RunningTime', kind: 'text' },
  { header: 'Ae3 Fuel Cons', key: 'ae3_FuelConsumption', kind: 'number' },
  { header: 'Total Fuel Cons', key: 'totalFuelCons', kind: 'number' },
];

export type ExportProfile = 'default' | 'ras';

const RAS_STYLE_COMPANY_NAMES = [
  'ras',
  'rejeki abadi sakti',
  'kanaka',
  'kanaka dwimitra manunggal',
  'brl',
  'baruna raya logistic',
];

export function isRasExportCompany(company?: { name?: string | null } | null): boolean {
  const name = (company?.name || '').trim().toLowerCase().replace(/\s+/g, ' ');
  return RAS_STYLE_COMPANY_NAMES.some((entry) => name.includes(entry));
}

// Ops mode names already live in EXPORT_COLUMNS. When an asset reports
// operation mode, only the fuel-cons figure is inserted beside that name.
const OPERATION_MODE_ANCHORS: {
  anchorKey: string;
  engine: OperationModeEngine;
  label: string;
  clutch?: boolean;
}[] = [
  { anchorKey: 'port_OperationMode', engine: 'port', label: 'Port' },
  { anchorKey: 'port_ClutchOperationMode', engine: 'port', label: 'Port', clutch: true },
  { anchorKey: 'starboard_OperationMode', engine: 'starboard', label: 'Stb' },
  {
    anchorKey: 'starboard_ClutchOperationMode',
    engine: 'starboard',
    label: 'Stb',
    clutch: true,
  },
  { anchorKey: 'center_OperationMode', engine: 'center', label: 'Center' },
  {
    anchorKey: 'center_ClutchOperationMode',
    engine: 'center',
    label: 'Center',
    clutch: true,
  },
];

function operationModeBucketKey(engine: OperationModeEngine, clutch?: boolean) {
  if (!clutch) return engine;
  return `clutch${engine.charAt(0).toUpperCase()}${engine.slice(1)}`;
}

function operationModeColumnsFor(
  bucket: Record<string, any>,
  item: (typeof OPERATION_MODE_ANCHORS)[number],
): ExportColumnDef[] {
  const bucketKey = operationModeBucketKey(item.engine, item.clutch);
  if (!bucket[bucketKey]) return [];

  return [
    {
      header: item.clutch
        ? `${item.label} Clutch Mode Fuel Cons`
        : `${item.label} Mode Fuel Cons`,
      key: `${bucketKey}_OperationModeConsRate`,
      kind: 'number',
      operationMode: { engine: item.engine, field: 'consRate', clutch: item.clutch },
    },
  ];
}

export function resolveExportColumns(
  operationMode?: Record<string, any> | null,
  profile: ExportProfile = 'default',
): ExportColumnDef[] {
  if (profile === 'ras') {
    return RAS_EXPORT_COLUMNS;
  }

  const firstKey = Object.keys(operationMode || {})[0];
  const firstBucket = firstKey
    ? (operationMode as Record<string, any>)[firstKey]
    : undefined;

  if (!firstBucket) {
    return EXPORT_COLUMNS;
  }

  return EXPORT_COLUMNS.flatMap((column) => {
    const extra = OPERATION_MODE_ANCHORS.filter(
      (item) => item.anchorKey === column.key,
    ).flatMap((item) => operationModeColumnsFor(firstBucket, item));

    return extra.length ? [column, ...extra] : [column];
  });
}

interface BuildExportRowsOptions {
  timezone: number;
  operationMode?: Record<string, any> | null;
  profile?: ExportProfile;
}

function extractRawValue(row: Record<string, any>, key: string): any {
  const value = row[key];
  if (value && typeof value === 'object') {
    if (value.props && value.props.children !== undefined) {
      return value.props.children;
    }
    return '';
  }
  return value;
}

function toNumberCell(value: any): number {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function toTextCell(value: any): string {
  if (value === undefined || value === null || value === '') {
    return '-';
  }
  return String(value);
}

function toCoordinateCell(
  row: Record<string, any>,
  key: string,
  toDMS: (value: any) => string,
): string {
  const raw = extractRawValue(row, key);
  const num = Number(raw);
  return toDMS(Number.isFinite(num) ? num : 0);
}

export function buildExportRows(
  dataSource: any[],
  options: BuildExportRowsOptions,
): Record<string, string | number>[] {
  if (!dataSource || !dataSource.length) {
    return [];
  }

  const columns = resolveExportColumns(options.operationMode, options.profile);
  const operationModeLookup = createBucketLookup<any>(options.operationMode);

  return dataSource.map((row) => {
    const record: Record<string, string | number> = {};

    columns.forEach((column) => {
      if (column.operationMode) {
        const bucket = operationModeLookup.get(row.timestamp);
        const bucketKey = operationModeBucketKey(
          column.operationMode.engine,
          column.operationMode.clutch,
        );
        const engine = bucket ? bucket[bucketKey] : undefined;
        const raw = engine ? engine[column.operationMode.field] : undefined;
        record[column.header] =
          column.kind === 'number' ? toNumberCell(raw) : toTextCell(raw);
        return;
      }

      if (column.key === 'dateTime') {
        record[column.header] = dayjs(row.timestamp * 1000)
          .utcOffset(options.timezone * 60)
          .format('DD MMM YYYY, HH:mm:ss');
        return;
      }

      if (column.key === 'gps_latitude') {
        record[column.header] = toCoordinateCell(row, 'gps_latitude', latToDMS);
        return;
      }

      if (column.key === 'gps_longitude') {
        record[column.header] = toCoordinateCell(row, 'gps_longitude', longToDMS);
        return;
      }

      if (column.key === AE_FUEL_CONS_KEY) {
        record[column.header] =
          toNumberCell(extractRawValue(row, 'ae1_FuelConsumption')) +
          toNumberCell(extractRawValue(row, 'ae2_FuelConsumption')) +
          toNumberCell(extractRawValue(row, 'ae3_FuelConsumption'));
        return;
      }

      const raw = extractRawValue(row, column.key);
      record[column.header] =
        column.kind === 'number' ? toNumberCell(raw) : toTextCell(raw);
    });

    return record;
  });
}

export function buildExportFileName(
  assetName: string,
  startAt: number,
  endAt: number,
  timezone: number,
): string {
  const startDate = reportDayjs(startAt, timezone).format('DD-MMM-YY-HHmm');
  const endDate = reportDayjs(endAt, timezone).format('DD-MMM-YY-HHmm');
  return `${assetName.split(' ').join('_')}-${startDate}_${endDate}.xlsx`;
}

function applyNumberFormats(
  worksheet: XLSX.WorkSheet,
  rowCount: number,
  columns: ExportColumnDef[],
) {
  columns.forEach((column, colIndex) => {
    if (column.kind !== 'number') return;

    for (let r = 1; r <= rowCount; r++) {
      const cellRef = XLSX.utils.encode_cell({ r, c: colIndex });
      const cell = worksheet[cellRef];
      if (cell) {
        cell.z = '0.00';
      }
    }
  });
}

const ExportDataReport: React.FC<IProps> = ({ dataSource, loading }) => {
  const { company } = useGroup();
  const { asset, filter, timezone } = useAppliedReportFilter();
  const { logs, speedUnit, distanceUnit, literUnit } = useReportLog();
  const [exporting, setExporting] = useState(false);
  const { activeDownload, startDownload, finishDownload } = useReportDownload();
  const isDownloadBusy = activeDownload !== null;

  async function onDownloadXlsxFromBackend() {
    if (!filter?.assetId || !filter.startAt || !filter.endAt) {
      return;
    }

    setExporting(true);
    try {
      const query = {
        assetId: filter.assetId,
        assetName: asset?.name,
        companyName: company?.name,
        startAt: filter.startAt,
        endAt: filter.endAt,
        interval: filter.interval,
        timezone,
        deviceType: filter.deviceType || undefined,
        deviceId: filter.deviceId || undefined,
        dataSource: Number(filter.dataSource) || 1,
        speedUnit,
        distanceUnit,
        literUnit,
        ...(filter.allowWeeklyMinute ? { allowWeeklyMinute: true } : {}),
      };
      const res: any = await httpRequest.get(
        `/report-log/export-xlsx${generateQueryString(query)}`,
        {
          responseType: 'blob',
          timeout: 15 * 60 * 1000,
        },
      );

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        buildExportFileName(asset.name, filter.startAt, filter.endAt, timezone),
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('failed export data log', error);
      message.error('Failed to export data log');
    } finally {
      setExporting(false);
    }
  }

  async function onDownloadXLSX() {
    const rangeError = getReportRangeError({ ...filter, timezone });

    if (rangeError) {
      message.error(rangeError);
      return;
    }

    if (exporting || !startDownload('xlsx')) {
      return;
    }

    try {
      await exportXlsx();
    } finally {
      finishDownload();
    }
  }

  async function exportXlsx() {
    if (USE_BACKEND_DATA_LOG_XLSX) {
      await onDownloadXlsxFromBackend();
      return;
    }

    if (!dataSource || !dataSource.length) {
      return;
    }

    setExporting(true);
    await new Promise((resolve) => setTimeout(resolve, 0));

    try {
      const profile: ExportProfile = isRasExportCompany(company) ? 'ras' : 'default';
      const operationMode = logs?.operation_mode;
      const columns = resolveExportColumns(operationMode, profile);
      const rows = buildExportRows(dataSource, { timezone, operationMode, profile });
      const headers = columns.map((column) => column.header);

      const worksheet = XLSX.utils.json_to_sheet(rows, { header: headers });
      applyNumberFormats(worksheet, rows.length, columns);

      worksheet['!cols'] = columns.map((column, index) => {
        if (index < 3) {
          const maxLen = rows.reduce((max, row) => {
            const value = row[column.header];
            const len =
              value === undefined || value === null ? 0 : String(value).length;
            return Math.max(max, len);
          }, column.header.length);
          return { width: maxLen };
        }
        return { width: 20 };
      });

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, worksheet, 'data');
      XLSX.writeFile(
        wb,
        buildExportFileName(asset.name, filter.startAt, filter.endAt, timezone)
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <Tooltip title='Download report log as .XLSX based on applied filter'>
      <Button
        color='success'
        loading={exporting}
        disabled={loading || exporting || isDownloadBusy}
        onClick={() => onDownloadXLSX()}
        type='primary'
        icon={<FileExcelFilled />}
      >
        Export XLSX
      </Button>
    </Tooltip>
  );
};

export default ExportDataReport;
